import { PERMISSIONS } from '@didto/domain';
import type { PermissionKey } from '@didto/domain';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmSheet,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  Notice,
  PageHeader,
  Sheet,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSession } from '@/app/session';
import { useCommand } from '@/app/useCommand';
import type { Row } from '@/lib/db';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import './settings.css';

/** A permission in words (its description); its key is for support, on hover. */
const PERMISSION_BY_KEY = new Map(PERMISSIONS.map((p) => [p.key, p]));
/** A permission area's name on screen. */
const CATEGORY_LABEL: Record<string, string> = {
  settings: 'Settings',
  employees: 'Staff',
  roles: 'Roles',
  devices: 'Devices',
  floor: 'Tables',
  production: 'Kitchen and bar',
  catalog: 'Catalog',
  audit: 'Audit log',
  reports: 'Reports',
  surfaces: 'Screens',
  orders: 'Orders',
  money: 'Money',
  shifts: 'Shifts and cash',
};
/** Keys in the order the domain lists them (by area), any unknown key last. */
const PERMISSION_ORDER = new Map(PERMISSIONS.map((p, i) => [p.key, i]));
const byDomainOrder = (a: PermissionKey, b: PermissionKey) =>
  (PERMISSION_ORDER.get(a) ?? Infinity) - (PERMISSION_ORDER.get(b) ?? Infinity) ||
  a.localeCompare(b);

type Role = Pick<Row<'role'>, 'id' | 'key' | 'name' | 'description' | 'is_system'>;
export function RolesPage() {
  const s = useSession();
  const canManage = s.can('role.manage');
  const cmd = useCommand([['roles'], ['grants']]);
  const [editing, setEditing] = useState<Role | 'new' | null>(null);

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('role')
        .select('id, key, name, description, is_system')
        .order('sort');
      if (error) throw error;
      return data;
    },
  });
  const grants = useQuery({
    queryKey: ['grants'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('role_permission')
        .select('role_id, permission_key');
      if (error) throw error;
      return data;
    },
  });
  const keysOf = (r: Role) =>
    new Set(
      (grants.data ?? [])
        .filter((g) => g.role_id === r.id)
        .map((g) => g.permission_key as PermissionKey),
    );
  const readError = roles.error ?? grants.error;

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Settings"
        title="Roles & permissions"
        subtitle="What the people in each role may do. Roles are yours to shape; the Owner role can always do everything."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setEditing('new')} data-testid="new-role">
              New role
            </Button>
          ) : undefined
        }
      />
      {readError && (
        <ErrorState
          title="Could not load the roles"
          message={errorText(readError)}
          onRetry={() => {
            if (roles.isError) void roles.refetch();
            if (grants.isError) void grants.refetch();
          }}
        />
      )}
      <div className="d-list" data-testid="role-list">
        {roles.isLoading && <Loading rows={3} label="Loading roles…" />}
        {(roles.data ?? []).map((r) => {
          const keys = [...keysOf(r)].sort(byDomainOrder);
          return (
            <div key={r.id} className="d-row">
              <div className="d-row__main d-stack d-stack--tight">
                <div className="d-row__title">
                  {r.name} {r.is_system && <Badge outline>system</Badge>}
                </div>
                {r.description && <div className="d-muted d-break">{r.description}</div>}
                {r.is_system ? (
                  <div>
                    <Badge tone="success">all permissions</Badge>
                  </div>
                ) : keys.length === 0 ? (
                  <span className="d-faint">no permissions</span>
                ) : (
                  <details className="settings__perms">
                    <summary>
                      {keys.length} {keys.length === 1 ? 'permission' : 'permissions'}
                    </summary>
                    <ul className="settings__perm-list">
                      {keys.map((k) => (
                        <li key={k}>
                          {PERMISSION_BY_KEY.get(k) ? (
                            <span title={k}>{PERMISSION_BY_KEY.get(k)!.description}</span>
                          ) : (
                            <span className="d-mono d-faint">{k}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              {/* ADR-025: the OWNER role changes only from an owner's signed-in account */}
              {canManage && (!r.is_system || s.canActOnOwners) && (
                <div className="d-actions">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing(r)}
                    data-testid={`edit-role-${r.key}`}
                  >
                    Edit
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        {roles.isSuccess && roles.data.length === 0 && <EmptyState title="No roles yet" />}
      </div>
      {editing && (
        <RoleSheet
          role={editing === 'new' ? null : editing}
          initial={editing === 'new' ? new Set() : keysOf(editing)}
          onClose={() => setEditing(null)}
          onSave={async (name, description, perms) => {
            if (editing === 'new')
              await cmd.mutateAsync({
                kind: 'role.create',
                payload: { name, description, permissions: perms },
              });
            else {
              await cmd.mutateAsync({
                kind: 'role.update',
                payload: { role_id: editing.id, name, description },
              });
              if (!editing.is_system)
                await cmd.mutateAsync({
                  kind: 'role.set_permissions',
                  payload: { role_id: editing.id, permissions: perms },
                });
            }
            setEditing(null);
          }}
          onDelete={
            editing !== 'new' && !editing.is_system
              ? async () => {
                  await cmd.mutateAsync({ kind: 'role.delete', payload: { role_id: editing.id } });
                  setEditing(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

function RoleSheet({
  role,
  initial,
  onClose,
  onSave,
  onDelete,
}: {
  role: Role | null;
  initial: Set<PermissionKey>;
  onClose: () => void;
  onSave: (name: string, description: string | null, perms: PermissionKey[]) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  // ADR-037: saving and deleting are commands; none starts while the server cannot be reached
  const online = useOnline();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [perms, setPerms] = useState<Set<PermissionKey>>(new Set(initial));
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const toggle = (k: PermissionKey) =>
    setPerms((p) => {
      const n = new Set(p);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  // the owner-only permissions (D5) are never offered; a category left empty is not shown
  const groups = [...new Set(PERMISSIONS.map((p) => p.category))]
    .map((category) => ({
      category,
      items: PERMISSIONS.filter((p) => p.category === category && !p.ownerOnly),
    }))
    .filter((g) => g.items.length > 0);
  return (
    <>
      <Sheet open onClose={onClose} title={role ? `Edit ${role.name}` : 'New role'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            void onSave(name.trim(), description.trim() || null, [...perms]).finally(() =>
              setBusy(false),
            );
          }}
          className="d-stack"
        >
          <Field label="Name">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
                autoFocus
                data-testid="role-name"
              />
            )}
          </Field>
          <Field label="Description">
            {(id) => (
              <Input
                id={id}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={300}
              />
            )}
          </Field>
          {role?.is_system ? (
            <Notice tone="info">The Owner role can always do everything.</Notice>
          ) : (
            groups.map(({ category, items }) => (
              <fieldset key={category} className="roles__group">
                <legend className="d-eyebrow">{CATEGORY_LABEL[category] ?? category}</legend>
                {items.map((p) => (
                  <div key={p.key} className="roles__perm">
                    <Checkbox
                      checked={perms.has(p.key)}
                      onChange={() => toggle(p.key)}
                      testId={`perm-${p.key}`}
                      label={
                        <span className="roles__perm-text" title={p.key}>
                          {p.description}
                        </span>
                      }
                    />
                  </div>
                ))}
              </fieldset>
            ))
          )}
          <div className="d-spread">
            {onDelete ? (
              <Button
                variant="danger"
                disabled={!online}
                onClick={() => setConfirmDelete(true)}
                data-testid="delete-role"
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={!online}
              data-testid="save-role"
            >
              Save
            </Button>
          </div>
        </form>
      </Sheet>
      {role && onDelete && (
        <ConfirmSheet
          open={confirmDelete}
          title={`Delete ${role.name}?`}
          confirmLabel="Delete role"
          busy={deleting}
          onClose={() => setConfirmDelete(false)}
          onConfirm={() => {
            setDeleting(true);
            // on success the role sheet closes with this one; a refusal is toasted
            void onDelete().catch(() => {
              setDeleting(false);
              setConfirmDelete(false);
            });
          }}
        >
          <p>The role and its permissions are removed. This cannot be undone.</p>
          <p className="d-muted">A role still given to an employee cannot be deleted.</p>
        </ConfirmSheet>
      )}
    </>
  );
}
